import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useOAuth } from "@/contexts/OAuthContext";
import AuthForm from "@/components/auth/AuthForm";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, UserPlus, ArrowRight, X } from "lucide-react";
import { supabase, loginGoogle, loginMicrosoft } from "@/lib/supa";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { posthog } from "@/lib/posthog";

// OAuth Error Modal Component
function OAuthErrorModal({ 
  isOpen, 
  onClose, 
  provider, 
  email, 
  onGoToRegister 
}: {
  isOpen: boolean;
  onClose: () => void;
  provider: string;
  email: string;
  onGoToRegister: () => void;
}) {
  const providerName = provider === 'google' ? 'Google' : provider === 'azure' ? 'Microsoft' : provider;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50">
              <UserPlus className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-left">Account Required</DialogTitle>
              <DialogDescription className="text-left">
                Create your Bloom account first
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="text-sm text-gray-700">
              <strong>{email}</strong> doesn't have a Bloom account yet.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              To use {providerName} sign-in, you'll need to create a Bloom account first with your email and password.
            </p>
          </div>
          
          <div className="space-y-3">
            <Button onClick={onGoToRegister} className="w-full" size="lg">
              <UserPlus className="mr-2 h-4 w-4" />
              Create Account
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            
            <Button onClick={onClose} variant="outline" className="w-full">
              <X className="mr-2 h-4 w-4" />
              Back to Login
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 text-center">
            After creating your account, you can sign in with {providerName}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Login() {
  const { login, user, error, isLoading, clearError, setOAuthContext } = useAuth();
  const { isOAuthRedirect, oauthState } = useOAuth();
  const [_, navigate] = useLocation();
  const [oauthError, setOauthError] = useState<{provider: string; email: string} | null>(null);
  
  // Get the return URL from query params
  const searchParams = new URLSearchParams(window.location.search);
  const returnUrl = searchParams.get('return');
  
  // Set OAuth context to login when this page loads
  useEffect(() => {
    console.log("Login page - Setting OAuth context to 'login'");
    setOAuthContext('login');
  }, [setOAuthContext]);
  
  // Handle OAuth-specific errors
  useEffect(() => {
    console.log("Login page - checking error:", error);
    console.log("Login page - current oauthError state:", oauthError);
    
    if (error && error.startsWith('OAUTH_NO_ACCOUNT:')) {
      console.log("Login page - OAuth error detected:", error);
      const parts = error.split(':');
      console.log("Login page - Error parts:", parts);
      if (parts.length === 3) {
        const errorData = {
          provider: parts[1],
          email: parts[2]
        };
        console.log("Login page - Setting OAuth error state:", errorData);
        setOauthError(errorData);
      }
    } else if (error) {
      // Log any other errors for debugging
      console.log("Login page - Non-OAuth error detected:", error);
      setOauthError(null);
    } else if (!error) {
      // Only clear oauth error if there's no error at all
      console.log("Login page - No error, clearing OAuth error state");
      // Don't automatically clear oauthError here to prevent flickering
    }
  }, [error]);

  // Add a separate effect to track when oauth error gets cleared unexpectedly
  useEffect(() => {
    if (oauthError) {
      console.log("OAuth error state is set:", oauthError);
      const timer = setTimeout(() => {
        console.log("OAuth error still showing after 5 seconds:", oauthError);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [oauthError]);

  // Enhanced debugging for OAuth processing state
  useEffect(() => {
    console.log("OAuth state changed:", oauthState);
    if (oauthState.isProcessing) {
      console.log("OAuth processing started - checking for session periodically");
      const checkInterval = setInterval(async () => {
        const { data } = await supabase.auth.getSession();
        console.log("OAuth processing check - has session:", !!data.session);
        if (!data.session) {
          console.log("No session found during OAuth processing");
        }
      }, 1000);
      
      // Stop checking after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        console.log("Stopped OAuth session checking");
      }, 10000);
      
      return () => clearInterval(checkInterval);
    }
  }, [oauthState]);
  
  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // Check for return URL from query params or session storage (for OAuth)
      const redirectUrl = returnUrl || sessionStorage.getItem('authReturnUrl');
      
      if (redirectUrl) {
        // Clear the stored URL
        sessionStorage.removeItem('authReturnUrl');
        navigate(redirectUrl);
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate, returnUrl]);
  
  // This function will be called with the form values from AuthForm
  const handleLogin = (email: string, password: string) => {
    // Identify user in PostHog before login
    posthog.identify(email, {
      email: email,
      login_method: 'email',
      $email: email,
      $name: email.split('@')[0], // Use email prefix as name
    });
    
    // The login function is provided by the useAuth hook
    login(email, password);
  };
  
  const handleGoToRegister = () => {
    setOauthError(null);
    clearError();
    navigate("/register");
  };
  
  const handleCloseOAuthError = () => {
    setOauthError(null);
    clearError();
  };
  
  if (user) {
    return null;
  }
  
  // Show loading state during OAuth processing
  if (isOAuthRedirect() || oauthState.isProcessing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Completing sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      {/* OAuth Error Modal */}
      {oauthError && (
        <OAuthErrorModal
          isOpen={true}
          onClose={handleCloseOAuthError}
          provider={oauthError.provider}
          email={oauthError.email}
          onGoToRegister={handleGoToRegister}
        />
      )}
      
      <Card className="w-[400px]">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            Sign in to your account
          </CardTitle>
          <CardDescription className="text-center">
            Or{" "}
            <Link to="/register" className="text-blue-600 hover:underline">
              create a new account
            </Link>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> OAuth sign-in requires an existing Bloom account. If you're new, please register first.
            </p>
          </div>

          <Button
            onClick={() => {
              // Store the return URL in session storage before OAuth redirect
              if (returnUrl) {
                sessionStorage.setItem('authReturnUrl', returnUrl);
              }
              // Identify user in PostHog for OAuth
              posthog.identify('oauth_user', {
                login_method: 'google',
                oauth_provider: 'google',
              });
              loginGoogle();
            }}
            className="w-full"
            variant="outline"
            size="lg"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              {/* Google icon SVG */}
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
              <path d="M1 1h22v22H1z" fill="none" />
            </svg>
            Continue with Google
          </Button>

          <Button
            onClick={() => {
              // Store the return URL in session storage before OAuth redirect
              if (returnUrl) {
                sessionStorage.setItem('authReturnUrl', returnUrl);
              }
              // Identify user in PostHog for OAuth
              posthog.identify('oauth_user', {
                login_method: 'microsoft',
                oauth_provider: 'microsoft',
              });
              loginMicrosoft();
            }}
            className="w-full"
            variant="outline"
            size="lg"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              {/* Microsoft icon SVG */}
              <path fill="#f35325" d="M11.4 11.4H1V1h10.4v10.4z" />
              <path fill="#81bc06" d="M23 11.4H12.6V1H23v10.4z" />
              <path fill="#05a6f0" d="M11.4 23H1V12.6h10.4V23z" />
              <path fill="#ffba08" d="M23 23H12.6V12.6H23V23z" />
            </svg>
            Continue with Microsoft
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Remove the outer form wrapper that was causing nested form issues */}
          {error && !error.startsWith('OAUTH_NO_ACCOUNT:') && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Authentication error</AlertTitle>
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}
          
          <AuthForm
            type="login"
            onSubmit={handleLogin}
            error={null} // We're showing errors above, so don't duplicate
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}
